import React from 'react';
import { Link } from 'react-router-dom';
import { services } from '../mock';
import { GraduationCap, Calculator, Building2, Briefcase, ArrowUpRight } from 'lucide-react';

const iconMap = { GraduationCap, Calculator, Building2, Briefcase };

const getOptimizedImageUrl = (url) => {
  if (url.includes('images.unsplash.com')) {
    return `${url}${url.includes('?') ? '&' : '?'}auto=format&fit=crop&w=700&q=75`;
  }

  if (url.includes('images.pexels.com')) {
    return `${url}${url.includes('?') ? '&' : '?'}auto=compress&cs=tinysrgb&w=700`;
  }

  return url;
};

const SectionHeader = ({ eyebrow, title, description }) => (
  <div className="text-center max-w-2xl mx-auto mb-14">
    {eyebrow && (
      <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#143324]/20 bg-[#f5f1e6] text-[11px] tracking-[0.2em] uppercase text-[#143324] font-body mb-6">
        <span className="w-1.5 h-1.5 rounded-full bg-[#143324]" />
        {eyebrow}
      </div>
    )}
    <h2 className="font-serif-display text-[#143324] text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
      {title}
    </h2>
    {description && (
      <p className="mt-5 text-[#3d3d3d] text-base md:text-[15px] leading-relaxed max-w-lg mx-auto font-body">
        {description}
      </p>
    )}
  </div>
);

const Services = () => {
  return (
    <section id="services" className="relative py-20 md:py-28">
      <div className="squiggle-bg" />
      <div className="max-w-6xl mx-auto px-6 relative">
        <SectionHeader
          eyebrow="What We Offer"
          title={<>Courses that <em className="italic">launch careers</em></>}
          description="From EA certification to your own US tax firm — explore programs designed to make you employable in 6 months."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {services.map((s) => {
            const Icon = iconMap[s.icon];
            return (
              <Link
                to={`/${s.slug === 'placement' ? 'ea-course' : s.slug}`}
                key={s.id}
                className="card-soft rounded-2xl p-6 md:p-7 group hover:shadow-md transition-shadow duration-300 block"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#e6e0cf] flex items-center justify-center">
                    {Icon && <Icon size={22} className="text-[#143324]" strokeWidth={1.6} />}
                  </div>
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity w-9 h-9 rounded-full bg-[#143324] text-[#f5f1e6] flex items-center justify-center">
                    <ArrowUpRight size={16} />
                  </span>
                </div>
                <h3 className="font-body font-semibold text-[#143324] text-xl md:text-2xl mb-3">
                  {s.title}
                </h3>
                <p className="text-[#3d3d3d] text-sm md:text-[14px] leading-relaxed mb-6 font-body">
                  {s.description}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {s.images.map((img, i) => (
                    <div key={i} className="aspect-[4/3] rounded-xl overflow-hidden">
                      <img
                        src={getOptimizedImageUrl(img)}
                        alt={s.title}
                        loading="lazy"
                        decoding="async"
                        width="700"
                        height="525"
                        sizes="(min-width: 768px) 260px, 50vw"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  ))}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
export { SectionHeader };
